import { PendingAttachment } from '../../../shared/utils/conversationAttachments';
import {
  ConversationInteractiveButtons,
  ConversationInteractiveList,
  ConversationMention,
  ConversationParticipantRef,
  Message,
  QuotedMessage,
} from '../types/Conversation';
import { idsEqual, upsertAndSortMessages } from './messageUtils';

export const OPTIMISTIC_MESSAGE_ID_PREFIX = 'pending:';
export const PENDING_VISITOR_SENDER_ID = 'pending-visitor';

export const isOptimisticMessageId = (messageId: string): boolean =>
  messageId.trim().toLowerCase().startsWith(OPTIMISTIC_MESSAGE_ID_PREFIX);

export const createOptimisticMessageId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${OPTIMISTIC_MESSAGE_ID_PREFIX}${crypto.randomUUID()}`;
  }

  return `${OPTIMISTIC_MESSAGE_ID_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

interface CreateOptimisticMessageParams {
  conversationId: string;
  sender: ConversationParticipantRef;
  content: string;
  messageType?: string;
  quotedMessageId?: string;
  quotedMessage?: QuotedMessage;
  interactiveList?: ConversationInteractiveList;
  interactiveButtons?: ConversationInteractiveButtons;
  mentions?: ConversationMention[];
  attachment?: PendingAttachment;
}

export const createOptimisticMessage = ({
  conversationId,
  sender,
  content,
  messageType = 'text',
  quotedMessageId,
  quotedMessage,
  interactiveList,
  interactiveButtons,
  mentions,
  attachment,
}: CreateOptimisticMessageParams): Message => ({
  id: createOptimisticMessageId(),
  conversationId,
  sender,
  content,
  timestamp: new Date().toISOString(),
  status: 'sent',
  source: 'internal',
  messageType,
  quotedMessageId,
  quotedMessage,
  interactiveList,
  interactiveButtons,
  mentions,
  mediaUrl: attachment?.previewUrl ?? undefined,
  mimeType: attachment?.file.type || undefined,
  fileName: attachment?.file.name || undefined,
  readBy: [],
  reactions: {},
});

export const replaceOptimisticMessage = (
  messages: Message[],
  optimisticId: string,
  confirmed: Message,
): Message[] => {
  const withoutOptimistic = messages.filter(message => !idsEqual(message.id, optimisticId));
  return upsertAndSortMessages(withoutOptimistic, confirmed);
};

export const replaceOptimisticMessages = (
  messages: Message[],
  replacements: Array<{ optimisticId: string; message: Message }>,
): Message[] => replacements.reduce(
  (current, replacement) => replaceOptimisticMessage(current, replacement.optimisticId, replacement.message),
  messages,
);

export const removeOptimisticMessages = (messages: Message[], optimisticIds: string[]): Message[] => {
  const removeKeys = new Set(optimisticIds.map(id => id.trim().toLowerCase()));
  return messages.filter(message => !removeKeys.has(message.id.trim().toLowerCase()));
};

const outboundSendersMatch = (optimistic: Message, confirmed: Message): boolean => {
  if (optimistic.sender.type !== confirmed.sender.type) {
    return false;
  }

  if (idsEqual(optimistic.sender.id, confirmed.sender.id)) {
    return true;
  }

  if (optimistic.sender.type !== 'customer') {
    return false;
  }

  if (idsEqual(optimistic.sender.id, PENDING_VISITOR_SENDER_ID)) {
    return true;
  }

  // Webchat optimistic messages use the visitor session id, while confirmed
  // messages use the linked contact person id after persistence.
  return optimistic.source === 'webchat'
    && (confirmed.source === 'webchat' || confirmed.source === 'internal');
};

const isLikelySameOutboundMessage = (optimistic: Message, confirmed: Message): boolean => {
  if (!isOptimisticMessageId(optimistic.id)) {
    return false;
  }

  if (!outboundSendersMatch(optimistic, confirmed)) {
    return false;
  }

  if (optimistic.content.trim() !== confirmed.content.trim()) {
    return false;
  }

  const optimisticTime = new Date(optimistic.timestamp).getTime();
  const confirmedTime = new Date(confirmed.timestamp).getTime();
  if (!Number.isFinite(optimisticTime) || !Number.isFinite(confirmedTime)) {
    return true;
  }

  return Math.abs(confirmedTime - optimisticTime) <= 2 * 60 * 1000;
};

export const removeRedundantOptimisticMessages = (messages: Message[], confirmed: Message): Message[] =>
  messages.filter(message => !isLikelySameOutboundMessage(message, confirmed));

export const stripRedundantOptimisticMessages = (messages: Message[]): Message[] =>
  messages.filter(message => {
    if (!isOptimisticMessageId(message.id)) {
      return true;
    }

    return !messages.some(
      candidate => !isOptimisticMessageId(candidate.id) && isLikelySameOutboundMessage(message, candidate),
    );
  });
